
import { EnumSelect } from "@/components/ui/EnumSelect";
import { Slider } from "@/components/ui/Slider";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type {
  SpaceDescriptor,
  ConditionsPresetKey,
  SpaceConditions,
  PrecipitationType,
  CloudCover,
  Visibility,
} from "@mind-sands/shared";
import {
  CONDITIONS_PRESETS,
  PRECIPITATION_TYPES,
  CLOUD_COVER_LEVELS,
  VISIBILITY_LEVELS,
  DEFAULT_SPACE_CONDITIONS,
} from "@mind-sands/shared";

/** Preset keys for the conditions dropdown */
const PRESET_KEYS = Object.keys(CONDITIONS_PRESETS) as ConditionsPresetKey[];

/** Resolve conditions to a full SpaceConditions object (from preset or object). */
function resolveConditions(
  conditions: SpaceDescriptor["conditions"],
): Required<SpaceConditions> {
  if (typeof conditions === "string") {
    return {
      ...DEFAULT_SPACE_CONDITIONS,
      ...(CONDITIONS_PRESETS[conditions as ConditionsPresetKey] ?? {}),
    };
  }
  return {
    precipitation:
      conditions?.precipitation ?? DEFAULT_SPACE_CONDITIONS.precipitation,
    cloudCover: conditions?.cloudCover ?? DEFAULT_SPACE_CONDITIONS.cloudCover,
    wind: conditions?.wind ?? DEFAULT_SPACE_CONDITIONS.wind,
    visibility: conditions?.visibility ?? DEFAULT_SPACE_CONDITIONS.visibility,
  };
}

/** Find the preset key matching a conditions object, or null if custom. */
function findMatchingPreset(
  conditions: SpaceDescriptor["conditions"],
): ConditionsPresetKey | null {
  if (typeof conditions !== "object" || conditions === null) return null;
  for (const [key, preset] of Object.entries(CONDITIONS_PRESETS)) {
    if (
      conditions.precipitation === preset.precipitation &&
      conditions.cloudCover === preset.cloudCover &&
      conditions.visibility === preset.visibility
    ) {
      return key as ConditionsPresetKey;
    }
  }
  return null;
}

// ─── Shared mini-primitives ────────────────────────────────────────────────

function MiniRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <label className="text-[10px] font-medium text-sands-400 uppercase tracking-wider w-20 shrink-0">
        {label}
      </label>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

// ─── ConditionsEditor ──────────────────────────────────────────────────────

interface ConditionsEditorProps {
  conditions: SpaceDescriptor["conditions"];
  onPresetChange: (preset: ConditionsPresetKey) => void;
  /** Called when a sub-field changes — receives the full updated SpaceConditions. */
  onConditionsChange: (conditions: SpaceConditions) => void;
}

/**
 * Atmospheric conditions editor.
 * Top row: preset dropdown (quick-apply common weather).
 * Sub-rows: individual axis editors (precipitation, cloud cover, visibility, wind).
 */
export function ConditionsEditor({
  conditions,
  onPresetChange,
  onConditionsChange,
}: ConditionsEditorProps) {
  const resolved = resolveConditions(conditions);
  const matchingPreset = findMatchingPreset(conditions);
  const isCustomObject =
    typeof conditions === "object" && conditions !== null;
  const [customizeOpen, setCustomizeOpen] = useState(isCustomObject);

  useEffect(() => {
    if (!isCustomObject) setCustomizeOpen(false);
  }, [isCustomObject]);

  const handlePresetChange = useCallback(
    (key: ConditionsPresetKey) => onPresetChange(key),
    [onPresetChange],
  );

  /** Update a single sub-field, emitting the full resolved object. */
  const updateField = useCallback(
    <K extends keyof SpaceConditions>(field: K, value: SpaceConditions[K]) => {
      onConditionsChange({ ...resolved, [field]: value });
    },
    [resolved, onConditionsChange],
  );

  return (
    <div className="mb-2">
      {/* Preset selector */}
      <MiniRow label="Preset">
        <EnumSelect
          variant="sm"
          value={
            typeof conditions === "string"
              ? conditions
              : (matchingPreset ?? undefined)
          }
          options={PRESET_KEYS}
          onChange={handlePresetChange}
        />
      </MiniRow>

      <button
        type="button"
        onClick={() => setCustomizeOpen((open) => !open)}
        className="mb-1 ml-2 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-sands-400 hover:text-sands-600"
      >
        <ChevronDown
          className={`h-3 w-3 transition-transform ${customizeOpen ? "" : "-rotate-90"}`}
        />
        Customize
      </button>

      {customizeOpen && (
        <div className="ml-2 pl-3 border-l-2 border-sands-200">
          <MiniRow label="Precip.">
            <EnumSelect
              variant="sm"
              value={resolved.precipitation}
              options={PRECIPITATION_TYPES}
              onChange={(v: PrecipitationType) =>
                updateField("precipitation", v)
              }
            />
          </MiniRow>
          <MiniRow label="Clouds">
            <EnumSelect
              variant="sm"
              value={resolved.cloudCover}
              options={CLOUD_COVER_LEVELS}
              onChange={(v: CloudCover) => updateField("cloudCover", v)}
            />
          </MiniRow>
          <MiniRow label="Visibility">
            <EnumSelect
              variant="sm"
              value={resolved.visibility}
              options={VISIBILITY_LEVELS}
              onChange={(v: Visibility) => updateField("visibility", v)}
            />
          </MiniRow>
          <MiniRow label="Wind">
            <Slider
              min={0}
              max={1}
              step={0.05}
              value={resolved.wind.strength}
              onChange={(e) =>
                updateField("wind", {
                  ...resolved.wind,
                  strength: parseFloat(e.target.value),
                })
              }
              title={`Wind: ${Math.round(resolved.wind.strength * 100)}%`}
            />
          </MiniRow>
        </div>
      )}
    </div>
  );
}
